import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PipelineService from 'services/PipelineService';
import BasicModal from '__components/Modals/presets/BasicModal';
import { getDataQAForInput } from 'components/Inputs/helpers';
import FieldInput from 'components/Fields/FieldInput';
import { getFieldInputValue } from 'utility/getFieldInputValue';
import { getSelectedOptions } from './helpers';

type Option = {
  selected:
    | { value: string; label: string }[]
    | { id: string; label: string }[];
  stageId: string;
  recordId: string;
};

type AddReasonLostModalProps = {
  disabled?: boolean;
  isRecordsEndpointData?: boolean;
  handleUpdateFieldOption(data: any): void;
  onConfirm(...args: any[]): void;
  onHide(): void;
  model: any;
  reasonLostField: any;
  reasonsDialogData: {
    record: any;
    stageId: string;
  }[];
  show: boolean;
};

const AddReasonLostModal = (props: AddReasonLostModalProps) => {
  const {
    disabled = false,
    handleUpdateFieldOption,
    isRecordsEndpointData = false,
    onConfirm,
    onHide,
    model,
    reasonLostField,
    reasonsDialogData,
    show,
  } = props;

  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  const reasonLostFieldRef = useRef(reasonLostField);

  useEffect(() => {
    if (reasonLostFieldRef.current) {
      if (isRecordsEndpointData) {
        const options = reasonsDialogData.map(({ record, stageId }) => {
          const selected = getFieldInputValue(
            reasonLostFieldRef.current,
            record
          ) as { value: string; label: string }[];
          return {
            selected,
            stageId,
            recordId: record.id,
          };
        });
        setSelectedOptions(options);
      } else {
        const options = getSelectedOptions(
          reasonsDialogData,
          reasonLostFieldRef.current
        );
        setSelectedOptions(options);
      }
    }
  }, [reasonsDialogData, isRecordsEndpointData]);

  return disabled || !reasonLostField ? null : (
    <BasicModal
      buttonText={t('Save')}
      heading={t('Add Reason(s) Lost')}
      leftBtn={null}
      onConfirm={() => onConfirm(selectedOptions, reasonLostField.id)}
      show={show}
      onHide={onHide}
      size="small"
    >
      {reasonsDialogData.map(({ record, stageId }, i) => {
        return (
          <FieldInput
            key={record.id}
            menuInline
            margin={i !== reasonsDialogData.length - 1}
            label={t('Choose Reason(s) Lost') + ' - ' + record.name}
            variant="outline"
            inModal
            disabled={!reasonLostField.access.edit}
            field={reasonLostField}
            fetchUrl={`custom-objects/${model.id}`}
            value={
              selectedOptions.find(
                (options) =>
                  options.stageId === stageId && options.recordId === record.id
              )?.selected
            }
            onChange={(selected: { id: string; label: string }[]) => {
              setSelectedOptions((prev) =>
                prev.map((data) =>
                  data.stageId === stageId && data.recordId === record.id
                    ? { ...data, selected }
                    : data
                )
              );
            }}
            serviceToUse={PipelineService}
            objectId={model.id}
            stages={model.pipeline.stages}
            {...getDataQAForInput(
              `custom-object-record-${reasonLostField.name}`,
              reasonLostField.fieldType
            )}
            handleUpdateFieldOption={handleUpdateFieldOption}
          />
        );
      })}
    </BasicModal>
  );
};

export default AddReasonLostModal;
